import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { applyProposal, type Proposal } from '@/lib/templates/api';
import { UNCERTAIN_APPLY_LINE, applyFailure } from '@/lib/templates/review-copy';
import { reportReviewFailure } from '@/lib/templates/report-review-failure';
import {
  applyBody,
  choose as chooseSection,
  chooseAll as chooseEverySection,
  chooseConflict as chooseConflictedSection,
  startReview,
  withResult,
  type Choice,
  type ReviewState,
} from '../utils/review-decisions';

interface TextReviewHandlers {
  onClosed: () => void;
  onReplaced: () => void;
  onPartiallyApplied: (proposalId: string) => void;
}

export interface TextReview {
  state: ReviewState;
  busy: boolean;
  error: string | null;
  choose: (key: string, c: Choice | null) => void;
  chooseAll: (c: Choice) => void;
  chooseConflict: (key: string, c: 'keep' | 'force' | null) => void;
  submit: () => void;
}

export function useTextReview(
  entityId: string,
  surfaceId: string,
  proposal: Proposal,
  handlers: TextReviewHandlers,
): TextReview {
  const [state, setState] = useState(() => startReview(proposal));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (state.proposalId !== proposal.proposalId) {
    setState(startReview(proposal));
    setError(null);
  }

  const latest = useRef({ state, proposal, handlers, busy });
  latest.current = { state, proposal, handlers, busy };
  const reportedPartial = useRef(new Set<string>());
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const choose = useCallback((key: string, c: Choice | null) => setState((s) => chooseSection(s, key, c)), []);
  const chooseAll = useCallback(
    (c: Choice) => setState((s) => chooseEverySection(s, latest.current.proposal, c)),
    [],
  );
  const chooseConflict = useCallback(
    (key: string, c: 'keep' | 'force' | null) => setState((s) => chooseConflictedSection(s, key, c)),
    [],
  );

  const submit = useCallback(() => {
    const { state: current, proposal: sent, busy: inFlight } = latest.current;
    if (inFlight || current.proposalId !== sent.proposalId) return;
    const proposalId = sent.proposalId;
    setBusy(true);
    latest.current.busy = true;
    void applyProposal(entityId, surfaceId, applyBody(current, sent)).then(
      (result) => {
        setBusy(false);
        setError(null);
        setState((s) => (s.proposalId === proposalId ? withResult(s, result) : s));
        if (
          result.state === 'open'
          && result.sections.some((section) => section.outcome === 'applied')
          && !reportedPartial.current.has(proposalId)
        ) {
          reportedPartial.current.add(proposalId);
          latest.current.handlers.onPartiallyApplied(proposalId);
        }
        if (result.state === 'closed' && mounted.current) latest.current.handlers.onClosed();
      },
      (failure: unknown) => {
        setBusy(false);
        reportReviewFailure(failure, 'text apply');
        const outcome = applyFailure(failure);
        if (!mounted.current) return;
        if (outcome === 'uncertain') {
          setError(UNCERTAIN_APPLY_LINE);
          latest.current.handlers.onReplaced();
          return;
        }
        if (outcome === 'closed' || outcome === 'removed') {
          latest.current.handlers.onClosed();
          return;
        }
        if (outcome === 'replaced') {
          latest.current.handlers.onReplaced();
          return;
        }
        setError(outcome.message);
      },
    );
  }, [entityId, surfaceId]);

  return useMemo(
    () => ({ state, busy, error, choose, chooseAll, chooseConflict, submit }),
    [state, busy, error, choose, chooseAll, chooseConflict, submit],
  );
}
